import { useEffect, useState } from "react";
import api, { BACKEND_URL, shortDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Upload, Download, FileText, Trash2, X } from "lucide-react";

const CATEGORIES = [
  "Product brochures", "Product specifications", "Training presentations",
  "Sales guides", "Price lists", "Warranty guides", "Product videos",
  "New product information", "Promotional documents", "Company procedures",
];

export default function TrainingDocs() {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["super_admin", "management", "general_manager"].includes(user?.role);

  const load = () => api.get("/training-docs", { params: category ? { category } : {} }).then(r => setDocs(r.data));
  useEffect(() => { load(); }, [category]);

  const del = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/training-docs/${id}`);
    toast.success("Deleted");
    load();
  };
  const download = async (d) => {
    const token = localStorage.getItem("mlily_token");
    window.open(`${BACKEND_URL}/api/training-docs/${d.id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="docs-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-black tracking-tight">Training Documents</h1>
          <p className="text-slate-600 mt-1">{docs.length} document{docs.length !== 1 ? "s" : ""} available</p>
        </div>
        <div className="flex gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="docs-category">
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          {isAdmin && (
            <button onClick={() => setShowUpload(true)} className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" data-testid="upload-doc-btn">
              <Upload className="w-4 h-4" /> Upload
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map(d => (
          <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-5 card-hoverable" data-testid={`doc-card-${d.id}`}>
            <FileText className="w-6 h-6 text-[#003DA5]" />
            <div className="mt-3 text-lg font-heading font-semibold">{d.title}</div>
            <div className="text-xs text-slate-500 mt-1">{d.category} • {shortDate(d.uploaded_at)}</div>
            <div className="text-xs text-slate-600 mt-2 line-clamp-2">{d.description}</div>
            {d.allowed_agent_ids?.length > 0 && <div className="text-[10px] text-slate-500 mt-2">Restricted to {d.allowed_agent_ids.length} agent{d.allowed_agent_ids.length !== 1 ? "s" : ""}</div>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => download(d)} className="flex-1 h-9 border border-slate-300 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#003DA5]" data-testid={`download-doc-${d.id}`}>
                <Download className="w-4 h-4" /> Download
              </button>
              {isAdmin && <button onClick={() => del(d.id)} className="h-9 w-9 border border-slate-300 rounded-lg grid place-items-center text-red-600 hover:border-red-600"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
        {docs.length === 0 && <div className="col-span-full text-center text-slate-500 py-16">No documents.</div>}
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
    </div>
  );
}

function UploadModal({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", category: "Product brochures", description: "" });
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/agents").then(r => setAgents(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Select a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", f.title);
      fd.append("category", f.category);
      if (f.description) fd.append("description", f.description);
      if (selectedAgents.length) fd.append("allowed_agent_ids", selectedAgents.join(","));
      await api.post("/training-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const toggleAgent = (id) => setSelectedAgents(selectedAgents.includes(id) ? selectedAgents.filter(x => x !== id) : [...selectedAgents, id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex justify-between sticky top-0 bg-white"><h3 className="text-xl font-heading font-semibold">Upload Training Document</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-4">
          <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Title *</label><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="doc-title" /></div>
          <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg">{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          </div>
          <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Description</label><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="mt-1 w-full p-3 border rounded-lg" /></div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Allowed Agents (empty = all)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {agents.map(a => (
                <button type="button" key={a.id} onClick={() => toggleAgent(a.id)} className={`px-3 py-1 rounded-full text-xs border ${selectedAgents.includes(a.id) ? "bg-[#001F54] text-white border-[#001F54]" : "border-slate-300"}`}>{a.name}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">File *</label>
            <input type="file" required onChange={(e) => setFile(e.target.files[0])} className="mt-1 w-full text-sm" data-testid="doc-file-input" />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" disabled={busy} className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-doc-btn">{busy ? "Uploading…" : "Upload"}</button></div>
      </form>
    </div>
  );
}
